import React, { useState } from 'react';
import { QAWidgetInstanceConfig, QADataContent, QAItem, ConvictionDataContent, ImageRepositoryDataContent } from '../../../../../types';
import { WidgetEmptyState } from '../../../design';

// Union type for all dashboard data content types
type DashboardDataContent = QADataContent | ConvictionDataContent | ImageRepositoryDataContent;

interface QAWidgetRendererProps {
  config: QAWidgetInstanceConfig;  // Uses instance config which includes dataKey
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: DashboardDataContent) => void;
  readOnly?: boolean;
}

const QAWidgetRenderer: React.FC<QAWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Get Q&A data from dashboard data using the dataKey
  const qaData = dashboardData[config.dataKey] as QADataContent | undefined;

  const toggleAccordion = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleAnswerChange = (itemId: string, newAnswer: string) => {
    if (!qaData || !onDataChange) return;

    const updatedItems = qaData.items.map((item) =>
      item.id === itemId ? { ...item, answer: newAnswer } : item
    );

    onDataChange(config.dataKey, {
      ...qaData,
      items: updatedItems,
    });
  };

  // No data key configured
  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  // No data found for the key
  if (!qaData || !qaData.items || qaData.items.length === 0) {
    return (
      <WidgetEmptyState
        title="No questions configured"
        description="Add questions in the Workflow Builder step configuration."
      />
    );
  }

  const renderAnswerField = (item: QAItem) => {
    const canEdit = !readOnly;
    const isEmpty = !item.answer?.trim();
    const showRequiredWarning = item.required && isEmpty && canEdit;

    const baseInputClasses = "w-full px-4 py-3 rounded-lg rounded-br-sm text-sm transition-all bg-surface-secondary border";
    const normalClasses = `${baseInputClasses} border-neutral-600 text-neutral-800 placeholder-neutral-400 focus:border-neutral-400 focus:outline-none resize-y`;
    const warningClasses = `${baseInputClasses} border-danger-500 text-neutral-800 placeholder-neutral-400 focus:border-danger-400 focus:outline-none resize-y`;
    const inputClasses = showRequiredWarning ? warningClasses : normalClasses;

    if (!canEdit) {
      return (
        <div className="px-4 py-3 bg-surface-secondary rounded-lg border border-neutral-600">
          <p className="text-sm text-neutral-600">
            {item.answer || <span className="italic text-neutral-500">No answer provided</span>}
          </p>
        </div>
      );
    }

    return (
      <div>
        <textarea
          value={item.answer || ''}
          onChange={(e) => handleAnswerChange(item.id, e.target.value)}
          placeholder="Enter your answer..."
          className={inputClasses}
          rows={3}
        />
        {showRequiredWarning && (
          <p className="text-xs text-danger-500 mt-1.5 font-medium">Required field</p>
        )}
      </div>
    );
  };

  const renderQuestion = (item: QAItem) => (
    <div className="mb-2">
      <p className="font-bold text-sm text-neutral-800">
        {item.question}
        {item.required && <span className="text-danger-500 ml-1">*</span>}
      </p>
      {item.description && (
        <p className="text-xs text-neutral-400 mt-0.5">{item.description}</p>
      )}
    </div>
  );

  const renderVerticalLayout = () => (
    <div className="space-y-5">
      {qaData.items.map((item) => (
        <div key={item.id}>
          {renderQuestion(item)}
          {renderAnswerField(item)}
        </div>
      ))}
    </div>
  );

  const renderHorizontalLayout = () => (
    <div className="space-y-4">
      {qaData.items.map((item) => (
        <div key={item.id} className="flex gap-5 items-start">
          <div className="flex-1">
            {renderQuestion(item)}
          </div>
          <div className="flex-1">
            {renderAnswerField(item)}
          </div>
        </div>
      ))}
    </div>
  );

  const renderAccordionLayout = () => (
    <div className="space-y-3">
      {qaData.items.map((item) => {
        const isExpanded = expandedItems.has(item.id);
        return (
          <div key={item.id} className="rounded-lg border border-neutral-600 overflow-hidden">
            <button
              onClick={() => toggleAccordion(item.id)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-neutral-700/30 transition-colors"
            >
              <span className="font-bold text-sm text-neutral-800 text-left">
                {item.question}
                {item.required && <span className="text-danger-500 ml-1">*</span>}
              </span>
              <svg
                className={`w-4 h-4 text-neutral-400 transition-transform flex-shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isExpanded && (
              <div className="px-4 py-3 border-t border-neutral-600">
                {item.description && (
                  <p className="text-xs text-neutral-400 mb-2">{item.description}</p>
                )}
                {renderAnswerField(item)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="h-full overflow-auto p-5 bg-surface">
      {/* Title and Description */}
      {(qaData.title || qaData.description) && (
        <div className="mb-5">
          {qaData.title && (
            <h3 className="text-base font-bold text-neutral-800">{qaData.title}</h3>
          )}
          {qaData.description && (
            <p className="text-sm text-neutral-400 mt-1">{qaData.description}</p>
          )}
        </div>
      )}

      {/* Q&A Content */}
      {config.layout === 'horizontal' && renderHorizontalLayout()}
      {config.layout === 'accordion' && renderAccordionLayout()}
      {(config.layout === 'vertical' || !config.layout) && renderVerticalLayout()}
    </div>
  );
};

export default QAWidgetRenderer;
